import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import DoneIcon from "@mui/icons-material/Done";
import EditIcon from "@mui/icons-material/Edit";
import StarIcon from "@mui/icons-material/Star";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppMenuButton } from "./components/AppMenuButton";
import { FolderTile } from "./components/FolderTile";
import { PageTile } from "./components/PageTile";
import { useHomeLayout } from "./hooks/useHomeLayout";
import { Banner } from "@/components/branding/Banner";
import PageHeader from "@/components/layout/PageHeader";
import { LicensedBoundary } from "@/components/LicensedBoundary";
import { groupPages, PageLink } from "@/constants/groupConfig";
import { useLicensedPages } from "@/hooks/pageLicenseCheck/useLicensedPages";
import {
  NAVIGATE_ADDIN_EVENT,
  type NavigateAddInDetail,
} from "@/utilities/navigation/inStructureNavigation";

/**
 * The home page, and the shell every other page runs inside.
 *
 * Each page of this add-in is its own entry point, but they are all imported here and swapped in
 * place, which is what lets a folder tile or a menu entry open one at all. It is also why the
 * page currently showing takes its header from here, nine dots and all.
 */
export default function HomePage() {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState<PageLink | null>(null);
  const [openFolderId, setOpenFolderId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [draggedPageUrl, setDraggedPageUrl] = useState<string | null>(null);

  const pages = useMemo(() => groupPages(), []);

  /**
   * Shows another page when one of them asks for it.
   *
   * Each page of this add-in is its own entry point, so a page cannot navigate anywhere by
   * itself — the launcher is the only thing that knows how to swap one for another. The map's
   * jump to the motor data of a vehicle goes through here.
   *
   * The page list is read inside the handler rather than closed over, so the listener is
   * attached once instead of on every render.
   */
  useEffect(() => {
    const handleNavigate = (event: Event) => {
      const requested = (event as CustomEvent<NavigateAddInDetail>).detail?.addInUrl;
      const target = groupPages().find(page => page.addInUrl === requested);
      if (target) {
        setCurrentPage(target);
        setEditing(false);
      }
    };

    window.addEventListener(NAVIGATE_ADDIN_EVENT, handleNavigate);
    return () => window.removeEventListener(NAVIGATE_ADDIN_EVENT, handleNavigate);
  }, []);

  /**
   * Which pages this reader may open, and whether that is settled.
   *
   * All of them at once rather than one answer per page: a folder saying "9 pages" that opens to
   * show seven would be lying, and the count is all a closed folder says.
   */
  const { pages: licensedPages, isSettled: allChecked } = useLicensedPages(pages);

  const licensedPageUrls = useMemo(
    () => licensedPages.map(page => page.addInUrl),
    [licensedPages],
  );

  const pagesByUrl = useMemo(
    () => new Map(licensedPages.map(page => [page.addInUrl, page])),
    [licensedPages],
  );

  const layout = useHomeLayout(licensedPageUrls);
  const openFolder = layout.folders.find(folder => folder.id === openFolderId) ?? null;

  const favoritePages = useMemo(
    () => layout.favoriteUrls.flatMap(url => pagesByUrl.get(url) ?? []),
    [layout.favoriteUrls, pagesByUrl],
  );

  const handleOpenPage = useCallback((page: PageLink) => {
    setCurrentPage(page);
    setEditing(false);
  }, []);

  const appMenuButton = (
    <AppMenuButton
      pagesByUrl={pagesByUrl}
      folders={layout.folders}
      favoriteUrls={layout.favoriteUrls}
      onOpen={handleOpenPage}
    />
  );

  const handleDropOnFolder = (folderId: string) => {
    if (draggedPageUrl) {
      layout.movePage(draggedPageUrl, folderId);
      setDraggedPageUrl(null);
    }
  };

  // ─── A page is open ──────────────────────────────────────────────

  if (currentPage) {
    const header = (
      <PageHeader
        handleHomeClick={() => {
          setCurrentPage(null);
          setOpenFolderId(null);
        }}
        appMenuButton={appMenuButton}
        title={currentPage.name}
        content={currentPage.component}
        docs={currentPage.docs}
      />
    );

    // The admin page is not sold as a product, so there is no licence to check for it.
    return currentPage.name === "Admin" ? (
      header
    ) : (
      <LicensedBoundary name={currentPage.groupName}>{header}</LicensedBoundary>
    );
  }

  // ─── The home page itself ────────────────────────────────────────

  const folderGrid = (
    <Grid container spacing={2}>
      {layout.folders.map(folder => (
        <Grid item xs={12} sm={6} md={3} key={folder.id}>
          <FolderTile
            folder={folder}
            pageCount={folder.pageUrls.length}
            editing={editing}
            deletable={layout.folders.length > 1}
            onOpen={setOpenFolderId}
            onRename={layout.renameFolder}
            onDelete={layout.deleteFolder}
            onMove={layout.moveFolder}
            onDropPage={editing ? handleDropOnFolder : undefined}
          />
        </Grid>
      ))}
    </Grid>
  );

  return (
    <PageHeader
      title={t("home.title", "Home")}
      appMenuButton={appMenuButton}
      content={
        <Box sx={{ position: "relative", height: "100%", overflow: "auto" }}>
          <Tooltip
            title={
              editing ? t("home.doneArranging", "Fertig") : t("home.arrange", "Ordner bearbeiten")
            }
          >
            <IconButton
              onClick={() => setEditing(current => !current)}
              aria-label={
                editing
                  ? t("home.doneArranging", "Fertig")
                  : t("home.arrange", "Ordner bearbeiten")
              }
              sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
            >
              {editing ? <DoneIcon /> : <EditIcon />}
            </IconButton>
          </Tooltip>

          <Stack sx={{ width: "min(1200px, 90%)", mx: "auto", mt: 4, pb: 6 }} spacing={4}>
            <Banner
              style={{
                width: "20%",
                minWidth: 200,
                marginLeft: "auto",
                marginRight: "auto",
                display: "flex",
              }}
            />

            {!allChecked || !layout.isFetched ? (
              // Also the arrangement, not just the licences: made editable before the stored
              // arrangement had arrived, the first star or rename counted as the reader's own
              // change and replaced everything they had filed.
              <CircularProgress sx={{ mx: "auto", mt: 6 }} />
            ) : openFolder ? (
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton
                    onClick={() => setOpenFolderId(null)}
                    aria-label={t("home.back", "Zurück")}
                  >
                    <ArrowBackIosNewIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {openFolder.name}
                  </Typography>
                </Stack>

                <Grid container spacing={2}>
                  {openFolder.pageUrls.flatMap(url => {
                    const page = pagesByUrl.get(url);
                    if (!page) {
                      return [];
                    }

                    return [
                      <Grid item xs={12} sm={6} md={3} key={url}>
                        <PageTile
                          page={page}
                          isFavorite={layout.favoriteUrls.includes(url)}
                          onOpen={handleOpenPage}
                          onToggleFavorite={layout.toggleFavorite}
                          onDragStart={editing ? setDraggedPageUrl : undefined}
                        />
                      </Grid>,
                    ];
                  })}
                </Grid>

                {editing ? (
                  <Stack spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                      {t("home.dragHint", "Eine Seite auf einen Ordner ziehen, um sie zu verschieben.")}
                    </Typography>
                    {folderGrid}
                  </Stack>
                ) : null}
              </Stack>
            ) : (
              <>
                {favoritePages.length > 0 ? (
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <StarIcon fontSize="small" sx={{ color: "warning.main" }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {t("home.favorites", "Favoriten")}
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      {favoritePages.map(page => (
                        <Grid item xs={12} sm={6} md={3} key={page.addInUrl}>
                          <PageTile
                            page={page}
                            isFavorite
                            onOpen={handleOpenPage}
                            onToggleFavorite={layout.toggleFavorite}
                            onMove={editing ? layout.moveFavorite : undefined}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                ) : null}

                <Stack spacing={1.5}>
                  {folderGrid}

                  {editing ? (
                    <Stack direction="row" spacing={1} sx={{ maxWidth: 420 }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={newFolderName}
                        onChange={event => setNewFolderName(event.target.value)}
                        onKeyDown={event => {
                          if (event.key === "Enter") {
                            layout.createFolder(newFolderName);
                            setNewFolderName("");
                          }
                        }}
                        label={t("home.folderName", "Ordnername")}
                      />
                      <Button
                        variant="outlined"
                        startIcon={<CreateNewFolderIcon />}
                        onClick={() => {
                          layout.createFolder(newFolderName);
                          setNewFolderName("");
                        }}
                      >
                        {t("home.createFolder", "Anlegen")}
                      </Button>
                    </Stack>
                  ) : null}
                </Stack>
              </>
            )}
          </Stack>
        </Box>
      }
    />
  );
}
